import { FC, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Checkbox,
  FormControlLabel,
  Button,
  Link,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import { ROOT } from 'routes';
import { writeLoginConsent } from 'api/LoginConsent';

/**
 * Consent page shown to users who have not yet accepted the Terms of Service,
 * Privacy Policy, and confirmed they are 13+ years old.
 */
const LoginConsent: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const fromParam = params.get('from') ?? undefined;

  const [agreeChecked, setAgreeChecked] = useState(false);
  const [ageChecked, setAgeChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allChecked = agreeChecked && ageChecked;

  const handleProceed = async () => {
    try {
      await writeLoginConsent();
      // Redirect to original destination or home
      const target = fromParam ? decodeURIComponent(fromParam) : ROOT;
      window.location.replace(target);
    } catch (e) {
      console.error('Consent submission failed', e);
      setError('Failed to submit consent. Please try again.');
    }
  };

  const handleCancel = () => {
    // Navigate to home, which will redirect to login due to missing consent
    navigate(ROOT);
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      px={2}
    >
      <Paper sx={{ p: 4, maxWidth: 600, width: '100%' }} elevation={3}>
        <Typography variant="h5" gutterBottom>
          Consent Required
        </Typography>
        <Typography gutterBottom>
          Before continuing, please review and accept our terms of service and privacy policy, and confirm
          that you are at least 13 years old.
        </Typography>
        <Box mt={2}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={agreeChecked}
                onChange={e => setAgreeChecked(e.target.checked)}
              />
            }
            label={
              <Typography>
                I agree to the{' '}
                <Link
                  href="/tos"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </Link>
                {' '}and have read the{' '}
                <Link
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </Link>
                .
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={ageChecked}
                onChange={e => setAgeChecked(e.target.checked)}
              />
            }
            label="I am at least 13 years old"
          />
        </Box>
        <Box display="flex" justifyContent="flex-end" mt={4}>
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleProceed}
            disabled={!allChecked}
            sx={{ ml: 2 }}
          >
            Proceed
          </Button>
        </Box>
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default LoginConsent;